/**
 * Identity + tenancy — wires the multitenant model (ADR-002) into the SDK.
 *
 * identifyUser(): called from completeAuthenticatedSession once
 * /access/me/context resolves. Sends identify({profileId, properties:{role}})
 * — NEVER firstName/lastName/email (the SDK type accepts them; we do not) —
 * then setGlobalProperties (tenant_id/tenant_tier/facility_id/environment),
 * then ready() on first call (flushes the SDK's native disabled:true queue),
 * then re-enables emission.
 *
 * resetAnalytics(): called from performLogout — clear() + suppress emission,
 * so a shared device can never blend two tenants (US-A4).
 */
import { getClient } from "./client";
import { devWarn } from "./devWarn";
import { analyticsEnv } from "./env";
import { __setIdentifiedForInternalUse } from "./track";
import type { UserDataResponse } from "../Interfaces/LoginInterface";

/** ready() flushes the pre-identify queue exactly once per app session. */
let readyCalled = false;

/**
 * Associate the current user + tenant with all subsequent events. Fail-open:
 * never throws, so it can never break the login flow it is wired into.
 */
export function identifyUser(ctx: UserDataResponse): void {
  try {
    const client = getClient();
    if (client === null) {
      return;
    }
    const role = ctx.user.roles?.[0];
    client.identify({
      profileId: ctx.user.id,
      properties: role ? { role } : {},
    });
    client.setGlobalProperties({
      tenant_id: ctx.tenant.id,
      tenant_tier: ctx.tenant.subscriptionTier,
      facility_id: ctx.user.defaultFacilityId,
      environment: analyticsEnv?.environment,
      app_version: process.env.REACT_APP_VERSION ?? "unknown",
    });
    if (!readyCalled) {
      client.ready();
      readyCalled = true;
    }
    __setIdentifiedForInternalUse(true);
  } catch (error) {
    devWarn("identifyUser failed", error);
  }
}

/** Clear all analytics identity + tenant state on logout. Fail-open. */
export function resetAnalytics(): void {
  try {
    __setIdentifiedForInternalUse(false);
    const client = getClient();
    if (client === null) {
      return;
    }
    client.clear();
  } catch (error) {
    devWarn("resetAnalytics failed", error);
  }
}

/** Test-only: resets the ready()-called-once latch between tests. */
export function __resetIdentityStateForTests(): void {
  readyCalled = false;
}
